import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ref, getDatabase, get } from "firebase/database";
import { getAuth, signInAnonymously } from "firebase/auth";

const PIN_LENGTH = 6;
const MAX_ATTEMPTS = 3;
const LOCKOUT_SECONDS = 30;

const KEYS = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
  ["", "0", "⌫"],
];

function WaiterLogin() {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [shake, setShake] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [lockout, setLockout] = useState(0); // seconds remaining
  const lockoutRef = useRef(null);
  const navigate = useNavigate();
  const database = getDatabase();

  useEffect(() => {
    return () => { if (lockoutRef.current) clearInterval(lockoutRef.current); };
  }, []);

  const startLockout = () => {
    setLockout(LOCKOUT_SECONDS);
    setPin("");
    lockoutRef.current = setInterval(() => {
      setLockout((prev) => {
        if (prev <= 1) {
          clearInterval(lockoutRef.current);
          setAttempts(0);
          setError("");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleKey = (key) => {
    if (lockout > 0) return;
    if (key === "⌫") {
      setPin((prev) => prev.slice(0, -1));
      setError("");
      return;
    }
    if (key === "") return;
    if (pin.length >= PIN_LENGTH) return;
    setPin((prev) => prev + key);
    setError("");
  };

  const handleConfirm = async () => {
    if (pin.length < PIN_LENGTH || lockout > 0) return;
    setLoading(true);
    try {
      const pinRef = ref(database, `pinMap/${pin}`);
      const snapshot = await get(pinRef);
      if (snapshot.exists()) {
        const data = snapshot.val();
        if (data.isActive) {
          await signInAnonymously(getAuth());
          localStorage.setItem("role", "waiter");
          localStorage.setItem("ownerId", data.ownerId);
          localStorage.setItem("waiterId", data.waiterId);
          localStorage.setItem("waiterName", data.name);
          localStorage.setItem("email", data.ownerEmail || "");
          navigate("/dashboard");
        } else {
          triggerError("This PIN has been deactivated. Contact your manager.", false);
        }
      } else {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        if (newAttempts >= MAX_ATTEMPTS) {
          triggerError(`Too many wrong attempts. Try again in ${LOCKOUT_SECONDS}s.`, true);
        } else {
          triggerError(`Invalid PIN. ${MAX_ATTEMPTS - newAttempts} attempt${MAX_ATTEMPTS - newAttempts === 1 ? "" : "s"} remaining.`, false);
        }
      }
    } catch {
      triggerError("Connection error. Please try again.", false);
    } finally {
      setLoading(false);
    }
  };

  const triggerError = (msg, doLockout) => {
    setError(msg);
    setPin("");
    setShake(true);
    setTimeout(() => setShake(false), 600);
    if (doLockout) startLockout();
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-surface px-6">
      <div className="w-full max-w-xs">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="logo.png" alt="WaitLessQ" className="w-14 h-14 rounded-2xl mx-auto mb-3" />
          <h1 className="text-xl font-bold text-gray-900">WaitLessQ</h1>
          <p className="text-sm text-gray-500 mt-1">Waiter Login</p>
        </div>

        {/* PIN dots */}
        <div className={`flex justify-center gap-3 mb-2 ${shake ? "animate-shake" : ""}`}
          style={shake ? { animation: "shake 0.5s ease-in-out" } : {}}>
          {Array.from({ length: PIN_LENGTH }).map((_, i) => (
            <div
              key={i}
              className={`w-4 h-4 rounded-full border-2 transition-all ${
                i < pin.length
                  ? "bg-primary border-primary"
                  : "bg-white border-gray-300"
              }`}
            />
          ))}
        </div>
        <p className="text-center text-xs font-medium text-gray-500 mb-1">
          Enter your 6-digit PIN
        </p>
        {error && (
          <p className="text-center text-xs text-red-500 font-medium mb-1">{error}</p>
        )}
        {lockout > 0 && (
          <p className="text-center text-xs text-orange-500 font-semibold mb-1">
            Locked for {lockout}s
          </p>
        )}

        {/* Keypad */}
        <div className="bg-white rounded-3xl shadow-card p-5 mt-4">
          <div className="flex flex-col gap-3">
            {KEYS.map((row, ri) => (
              <div key={ri} className="flex gap-3">
                {row.map((key, ki) => (
                  <button
                    key={ki}
                    onClick={() => handleKey(key)}
                    disabled={key === ""}
                    className={`flex-1 h-14 rounded-2xl text-xl font-bold transition-all select-none ${
                      key === ""
                        ? "invisible"
                        : key === "⌫"
                        ? "bg-gray-100 text-gray-600 hover:bg-gray-200 active:scale-95"
                        : "bg-primary-bg text-primary hover:bg-purple-100 active:scale-95 active:bg-primary active:text-white"
                    }`}
                  >
                    {key}
                  </button>
                ))}
              </div>
            ))}
          </div>

          {/* Confirm button */}
          <button
            onClick={handleConfirm}
            disabled={pin.length < PIN_LENGTH || loading || lockout > 0}
            className={`w-full mt-4 py-4 rounded-2xl text-sm font-bold transition-all ${
              pin.length === PIN_LENGTH && !loading && lockout === 0
                ? "bg-primary text-white hover:bg-primary-light"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            }`}
          >
            {loading ? "Verifying..." : lockout > 0 ? `Try again in ${lockout}s` : "Confirm"}
          </button>
        </div>

        {/* Back link */}
        <button
          onClick={() => navigate("/")}
          className="w-full text-center text-sm text-gray-500 mt-5 hover:text-primary transition-colors"
        >
          ← Back to Admin Login
        </button>
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20%, 60% { transform: translateX(-8px); }
          40%, 80% { transform: translateX(8px); }
        }
        .animate-shake { animation: shake 0.5s ease-in-out; }
      `}</style>
    </div>
  );
}

export default WaiterLogin;
